import { Icon } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useNavigate } from 'react-router'
import iconAdminDev from '~/assets/icons-light-theme/icon-adminDev-dark900.svg?react'
import iconLifeSupport from '~/assets/icons-light-theme/icon-lifeSupport-dark900.svg?react'
import iconOpenLink from '~/assets/icons-light-theme/icon-openLink-dark900.svg?react'
import iconSettings from '~/assets/icons-light-theme/icon-settings-dark900.svg?react'
import iconThoughtCloud from '~/assets/icons-light-theme/icon-thoughtCloud-feedback-dark900.svg?react'
import downChevron from '~/assets/svgs/icon-down-chevron-bold.svg?react'
import iconDownload from '~/assets/svgs/icon-download.svg?react'
import iconLogout from '~/assets/svgs/icon-logout-20.svg?react'
import MangoLogo from '~/assets/svgs/icon-mango-logo.svg?react'
import iconSwagger from '~/assets/svgs/icon-swagger.svg?react'
import iconSwapArrows from '~/assets/svgs/icon-swap-arrows.svg?react'
import upChevron from '~/assets/svgs/icon-up-chevron-bold.svg?react'
import { useUserState } from '~/atoms/UserState'
import { MangoButtonVariant } from '~/components/shared/componentStyles/MangoButton.style'
import { DropdownMangoButton } from '~/components/shared/Dropdown/DropdownMangoButton'
import { MenuPlacement } from '~/components/shared/Dropdown/Menu'
import { MenuItem, MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import urls from '~/constants/urls'
import { useLogout } from '~/hooks/useLogout'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { Environment } from '~/types/EnvironmentTypes'
import { buildEnvironment, getApiDocsUrl, isElectron } from '~/utils/common'
import { toNounCasing } from '~/utils/formatter'
import { openInNewTab } from '~/utils/functions'

import { useAccountButtonStyle } from './componentStyles'
import { AccountButtonStyleValues } from './componentStyles/AccountButton.style'

export const AccountButton = () => {
  const { sessionUser } = useUserState()
  const firstLast = !sessionUser
    ? ''
    : toNounCasing(`${sessionUser.first_name} ${sessionUser.last_name}`)
  const handleLogout = useLogout()

  const navigate = useNavigate()
  const [isMenuVisible, setIsMenuVisible] = React.useState(false)

  const styles = useAccountButtonStyle()

  const getMenuItem = (
    primaryText: string,
    leftIcon: SVGComponent,
    onClick: () => void,
    rightIcon?: SVGComponent,
    hide?: boolean,
  ) => {
    return {
      value: primaryText,
      content: (
        <MenuListItem
          primaryText={primaryText}
          leftContent={<Icon as={leftIcon} sx={styles.leftIcon} />}
          rightContent={
            rightIcon && <Icon as={rightIcon} sx={styles.rightIcon} />
          }
          hide={hide}
        />
      ),
      onSelect: onClick,
    } as MenuItem<string>
  }

  const handleMenuOpenChange = (isOpen: boolean) => {
    setIsMenuVisible(isOpen)
  }

  const menuItemData = useMemo(() => {
    const list = [
      {
        onClick: () => navigate(ApplicationRoutes.SETTINGS),
        icon: iconSettings,
        text: 'Settings',
      },
      {
        onClick: () => navigate(ApplicationRoutes.SETTINGS),
        icon: iconSwapArrows,
        text: 'Switch PBX',
      },
      {
        onClick: () => openInNewTab('mango://settings'),
        icon: MangoLogo,
        text: 'Open App',
        rightIcon: iconOpenLink,
        hide: isElectron,
      },
      {
        onClick: () => openInNewTab(urls.DESKTOP_APP_DOWNLOAD),
        icon: iconDownload,
        text: 'Download App',
        rightIcon: iconOpenLink,
        hide: isElectron,
      },
      {
        onClick: () => openInNewTab(urls.FEEDBACK),
        icon: iconThoughtCloud,
        text: 'Give Feedback',
        rightIcon: iconOpenLink,
      },
      {
        onClick: () => openInNewTab(urls.HELP_CENTER),
        icon: iconLifeSupport,
        text: 'Help Center',
        rightIcon: iconOpenLink,
      },
      {
        onClick: () => openInNewTab(urls.ADMIN),
        icon: iconAdminDev,
        text: 'Admin Portal',
        rightIcon: iconOpenLink,
      },
      {
        onClick: () => {
          void handleLogout()
        },
        icon: iconLogout,
        text: 'Log Out',
      },
    ]

    if (buildEnvironment === Environment.LOCAL) {
      // Insert API Docs link before the last item
      list.splice(-1, 0, {
        onClick: () => openInNewTab(getApiDocsUrl()),
        icon: iconSwagger,
        text: 'API Docs',
        rightIcon: iconOpenLink,
      })
    }

    return list
  }, [handleLogout])

  const menuItems = useMemo(() => {
    return menuItemData.map((item) => {
      return getMenuItem(
        item.text,
        item.icon,
        item.onClick,
        item.rightIcon,
        item.hide ?? false,
      )
    })
  }, [menuItemData])

  return (
    <div data-testid="account-button">
      <DropdownMangoButton
        variant={MangoButtonVariant.Default_200}
        label={firstLast}
        rightIcon={isMenuVisible ? upChevron : downChevron}
        mangoButtonOptions={{
          rightIconProps: { boxSize: AccountButtonStyleValues.chevronBoxSize },
        }}
        items={menuItems}
        onMenuOpenChange={handleMenuOpenChange}
        menuOptions={{
          placement: MenuPlacement.BottomRight,
          width: AccountButtonStyleValues.menuWidth,
          maxHeight: AccountButtonStyleValues.menuMaxHeight,
        }}
      />
    </div>
  )
}
